import type { TowerKind } from '../data/towers'

export type BalanceTrack = 'CONTROL' | 'ARSENAL_PARITY'

export interface BalanceTrackDefinition {
  readonly id: BalanceTrack
  readonly name: string
  readonly codename: string
  readonly description: string
  readonly adjustments: readonly string[]
}

const STORAGE_KEY = 'block-defense.balance-track.v1'

export const BALANCE_TRACKS: Record<BalanceTrack, BalanceTrackDefinition> = {
  CONTROL: {
    id: 'CONTROL', name: 'Control Grid', codename: 'RC-A',
    description: 'Unmodified release-candidate economy used as the comparison baseline.',
    adjustments: ['SOL $600', 'ETH $1,200', 'BTC $2,800'],
  },
  ARSENAL_PARITY: {
    id: 'ARSENAL_PARITY', name: 'Arsenal Parity', codename: 'RC-B',
    description: 'Tests earlier access to specialist towers without changing damage, enemies, or rewards.',
    adjustments: ['SOL $600', 'ETH $1,100', 'BTC $2,600'],
  },
}

class BalanceExperiment {
  private track = this.load()
  private listeners = new Set<(track: BalanceTrack) => void>()

  get active(): BalanceTrack { return this.track }

  set(track: BalanceTrack): BalanceTrack {
    this.track = track
    try { window.localStorage.setItem(STORAGE_KEY, track) } catch { /* storage can be unavailable */ }
    this.listeners.forEach((listener) => listener(track))
    return track
  }

  towerCost(kind: TowerKind, baseCost: number): number {
    if (this.track !== 'ARSENAL_PARITY') return baseCost
    if (kind === 'ETH') return 1_100
    if (kind === 'BTC') return 2_600
    return baseCost
  }

  subscribe(listener: (track: BalanceTrack) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private load(): BalanceTrack {
    try { return window.localStorage.getItem(STORAGE_KEY) === 'ARSENAL_PARITY' ? 'ARSENAL_PARITY' : 'CONTROL' } catch { return 'CONTROL' }
  }
}

export const balanceExperiment = new BalanceExperiment()
